import type { Reports } from './types';
const n = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const list = (v: unknown) => (Array.isArray(v) ? (v as Record<string, unknown>[]) : []);

/** Accept both report field sets present in the repository's RPC contracts. */
export function normalizeReports(raw: unknown): Reports {
  const data = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const summary = (data.summary ?? {}) as Record<string, unknown>;

  return {
    summary: {
      totalCalls: n(summary.total_calls),
      connected: n(summary.connected),
      missed: n(summary.missed),
      inbound: n(summary.inbound),
      outbound: n(summary.outbound),
      totalDuration: n(summary.total_duration),
      avgDuration: n(summary.avg_duration),
    },
    employees: list(data.employee_performance)
      .map((e) => ({
        employeeId: n(e.employee_id),
        name: String(e.employee_name ?? 'Unknown'),
        total: n(e.total),
        inbound: n(e.inbound),
        outbound: n(e.outbound),
        connected: n(e.connected),
        totalDuration: n(e.total_duration),
        avgHandleTime: n(e.avg_handle_time ?? e.avg_duration),
      }))
      .sort((a, b) => b.total - a.total),
    missed: list(data.missed_opportunities).map((m, i) => ({
      id: (m.id as string | number) ?? i,
      name: String(m.name ?? 'Unknown'),
      phone: String(m.phone ?? ''),
      missedAt: String(m.missed_at ?? m.missed_at_formatted ?? ''),
      employeeName: String(m.originally_routed_to ?? '—'),
      employeeId: m.employee_id == null ? null : n(m.employee_id),
      returned: String(m.status ?? '').toLowerCase() === 'returned',
      latencySeconds: n(m.latency_seconds),
    })),
    missedTotals:
      data.missed_totals && typeof data.missed_totals === 'object'
        ? {
            all: n((data.missed_totals as Record<string, unknown>).all),
            unreturned: n((data.missed_totals as Record<string, unknown>).unreturned),
          }
        : null,
    heatmap: list(data.activity_heatmap)
      .map((h) => ({
        day: h.day_num != null ? n(h.day_num) : n(h.day_of_week) + 1,
        hour: n(h.hour_num ?? h.hour),
        calls: n(h.call_volume ?? h.count),
      }))
      .filter((h) => h.day >= 1 && h.day <= 7 && h.hour >= 0 && h.hour <= 23),
    trend: list(data.daily_trend)
      .map((d) => ({ date: String(d.date ?? ''), totalCalls: n(d.total_calls) }))
      .filter((d) => d.date)
      .sort((a, b) => a.date.localeCompare(b.date)),
  };
}
